import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n.jsx";
import { useAuth } from "../context/AuthContext";
import { PageHeader, StatusPill } from "../components/Bits";
import { Button } from "../components/ui/button";
import { ArrowLeft, Phone } from "lucide-react";
import { Avatar, PersonalData, SizesForm, BadgePanel, PropertyList, MonthStats, EmployeeReports, HistoryList } from "../components/EmployeeBits";

const TABS = ["main", "work", "reports", "property", "sizes", "badge", "history"];

export default function EmployeeCard() {
  const { id } = useParams(); const nav = useNavigate();
  const { t, tn } = useI18n(); const { can, user } = useAuth();
  const [emp, setEmp] = useState(null); const [err, setErr] = useState(null); const [tab, setTab] = useState("main");
  const load = useCallback(() => api.get(`/employees/${id}/profile`).then((r) => setEmp(r.data)).catch((e) => setErr(e.response?.status || 500)), [id]);
  useEffect(() => { load(); }, [load]);
  if (err) return <div className="tactical-card p-6 text-center" data-testid="emp-card-error">{err === 403 ? t("no_access") : t("not_found")}</div>;
  if (!emp) return null;
  const active = !emp.archived && (emp.status || "active") === "active";
  return (
    <div className="max-w-3xl mx-auto space-y-3 min-w-0" data-testid="employee-card">
      <button onClick={() => nav(-1)} className="text-sm text-[var(--blue)] flex items-center gap-1" data-testid="emp-back"><ArrowLeft size={14} /> {t("back")}</button>
      <div className="tactical-card p-4 flex items-center gap-3 min-w-0">
        <Avatar emp={emp} size={56} />
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold text-lg truncate" data-testid="emp-card-name">{emp.first_name} {emp.last_name}</div>
          <div className="text-xs text-[var(--text-3)] truncate">{tn(emp.position?.name) || "—"} · <span className="font-mono-num">№ {emp.employee_number}</span></div>
          {emp.phone && can("employees.view") && <a href={`tel:${emp.phone}`} className="text-sm text-[var(--blue)] flex items-center gap-1 mt-0.5" data-testid="emp-phone"><Phone size={13} /> {emp.phone}</a>}
        </div>
        <StatusPill status={active ? "active" : "blocked"} />
      </div>
      <div className="flex gap-1 overflow-x-auto pb-1 -mx-1 px-1" data-testid="emp-tabs">
        {TABS.map((k) => <button key={k} onClick={() => setTab(k)} className={`h-9 px-3 rounded-md border text-xs font-semibold whitespace-nowrap ${tab === k ? "bg-[var(--sidebar)] text-white border-[var(--sidebar)]" : "tactical-card"}`} data-testid={`emp-tab-${k}`}>{t(`etab_${k}`)}</button>)}
      </div>
      {tab === "main" && <PersonalData emp={emp} reload={load} selfView={emp.id === user?.id} />}
      {tab === "work" && <MonthStats userId={emp.id} />}
      {tab === "reports" && <EmployeeReports userId={emp.id} />}
      {tab === "property" && <PropertyList emp={emp} />}
      {tab === "sizes" && <SizesForm emp={emp} reload={load} selfView={emp.id === user?.id} />}
      {tab === "badge" && <BadgePanel emp={emp} reload={load} selfView={emp.id === user?.id} />}
      {tab === "history" && <HistoryList userId={emp.id} />}
      {can("employees.edit") && tab === "main" && <Button variant="outline" onClick={() => nav("/employees", { state: { edit: emp.id } })} className="w-full h-10" data-testid="emp-open-edit">{t("edit")}</Button>}
    </div>
  );
}
